import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildEndpoint, defaultDataTransform, useDataFetcher, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { AsyncStatus } from 'Models';
import FormSection from 'v3/Components/Form/FormSection';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import {
  ContactInformationActive,
  ContactInformationReadOnly,
  ContactInformationData,
} from './ReviewerProfile.Overview.ContactInformation';
import {
  LicenseInformationActive,
  LicenseInformationReadOnly,
  LicenseInformationData,
  StateLicense,
  BoardCertification,
  hasExpiredLicenseOrCertification,
} from './ReviewerProfile.Overview.LicenseInformation';
import Banner from 'v3/Components/Shared/Banner';
import { RatesActive, RatesReadOnly, RatesData, FinancialDocument } from './ReviewerProfile.Overview.Rates';
import UnavailabilityContent, { UnavailabilityEntry } from './ReviewerProfile.Overview.Unavailability';
import {
  InternalNoteActive,
  InternalNoteReadOnly,
  InternalNoteData,
} from './ReviewerProfile.Overview.InternalNote';
import ImeLocationsContent, { ImeLocation } from './ReviewerProfile.Overview.ImeLocations';
import CaseManagementContent, { CaseManagementData } from './ReviewerProfile.Overview.CaseManagement';
import { ReviewerDocumentRow } from './ReviewerProfile.Documents.Drawer';
import { ReviewerProfileModel } from './useReviewerProfileForm';
import {
  REVIEWER_DOCUMENT_TYPE_CONTRACT,
  REVIEWER_DOCUMENT_TYPE_ADDENDUM,
  REVIEWER_DOCUMENT_TYPE_FEE_SCHEDULE,
} from './ReviewerProfile.ProfileCompleteness';
import './ReviewerProfile.Overview.scss';

const SkeletonCard: React.FC<{ lines?: number[]; extraHeight?: boolean }> = ({
  lines = [3, 3],
  extraHeight,
}) => (
  <div className="rp-skeleton-card" style={extraHeight ? { minHeight: 180 } : undefined}>
    <div className="rp-skeleton-card__title" />
    <div className="rp-skeleton-card__row">
      {lines.map((count, colIdx) => (
        <div key={colIdx} className="rp-skeleton-card__col">
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className={`rp-skeleton-card__line rp-skeleton-card__line--${
                i % 3 === 0 ? 'short' : i % 3 === 1 ? 'medium' : 'long'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const ReviewerProfileOverviewSkeleton: React.FC = () => (
  <div className="rp-skeleton-overview">
    <SkeletonCard lines={[4, 4]} />
    <SkeletonCard lines={[3, 3]} />
    <div className="rp-skeleton-right-top">
      <SkeletonCard lines={[2]} />
      <SkeletonCard lines={[3]} extraHeight />
    </div>
    <SkeletonCard lines={[1]} extraHeight />
  </div>
);

const Sections = {
  LicenseInformation: 'License Information',
  ContactInformation: 'Contact Information',
  Unavailability: 'Upcoming Unavailability',
  Rates: 'Rates',
  InternalNote: 'Internal Note',
};

const getLatestByType = (docs: ReviewerDocumentRow[], typeName: string): FinancialDocument | undefined => {
  const matches = docs
    .filter((d) => d.documentType?.name === typeName)
    .sort((a, b) => b.uploadDate.localeCompare(a.uploadDate));
  if (!matches.length) return undefined;
  const { id, title, fileName } = matches[0];
  return { id, title, fileName };
};

const EMPTY_CONTACT: ContactInformationData = {};
const EMPTY_RATES: RatesData = {};
const EMPTY_LICENSE: LicenseInformationData = {};
const EMPTY_UNAVAILABILITY: UnavailabilityEntry[] = [];
const EMPTY_INTERNAL_NOTE: InternalNoteData = {};
const EMPTY_IME_LOCATIONS: ImeLocation[] = [];
const EMPTY_CASE_MANAGEMENT: CaseManagementData = {
  casesOut: 0,
  casesComplete: 0,
  casesRejected: 0,
  rejectionRatePercent: 0,
};

// ReviewerLifecycleStatus: Active=4, Archived=5
const isActiveOrArchived = (statusValue?: number) =>
  statusValue !== undefined && statusValue >= 4;

enum ReviewerType {
  PeerReview = 1,
  Ime = 2,
  PeerReviewAndIme = 3,
}

const isImeType = (typeValue?: number) =>
  typeValue === ReviewerType.Ime || typeValue === ReviewerType.PeerReviewAndIme;

type ReviewerProfileOverviewProps = {
  reviewer?: ReviewerProfileModel;
  contactData?: ContactInformationData;
  onContactSaved?: (updated: ContactInformationData) => void;
  isContactLoading?: boolean;
  licenseData?: LicenseInformationData;
  onLicenseRefetch?: () => void;
  onCredentialsSaved?: (stateLicenses: StateLicense[], boardCertifications: BoardCertification[]) => void;
  isLicenseLoading?: boolean;
  ratesData?: RatesData;
  onRatesRefetch?: () => void;
  documents?: ReviewerDocumentRow[];
};

const ReviewerProfileOverview: React.FC<ReviewerProfileOverviewProps> = ({
  reviewer,
  contactData: contactDataProp,
  onContactSaved,
  isContactLoading: isContactLoadingProp,
  licenseData: licenseDataProp,
  onLicenseRefetch,
  onCredentialsSaved,
  isLicenseLoading: isLicenseLoadingProp,
  ratesData: ratesDataProp,
  onRatesRefetch,
  documents: documentsProp,
}) => {
  const { id } = useParams<{ id: string; tab?: string }>();
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const reviewerStatusValue = reviewer?.status?.value as number | undefined;
  const showCaseManagement = isActiveOrArchived(reviewerStatusValue);

  const hasPermissionsV3 = useHasPermissionsV3();
  const canEditContact = hasPermissionsV3(PermissionCodesV3.Reviewer.Profile.ContactEdit);
  const canEditRates = hasPermissionsV3(PermissionCodesV3.Reviewer.Profile.RatesEdit);
  const canEditInternalNote = hasPermissionsV3(PermissionCodesV3.Reviewer.Profile.InternalNoteEdit);

  const [licenseDataLocal, licenseStatus, { getData: refetchLicenseLocal, setData: setLicenseDataLocal }] = useDataFetcher<LicenseInformationData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'licenseInformation'),
    EMPTY_LICENSE,
    defaultDataTransform,
    undefined,
    !!id && !licenseDataProp,
    '3.0'
  );

  const licenseData = licenseDataProp ?? licenseDataLocal;
  const isLicenseLoadingLocal = isLicenseLoadingProp ??
    (licenseStatus === AsyncStatus.Idle || licenseStatus === AsyncStatus.Pending);

  const refetchLicense = () => {
    refetchLicenseLocal();
    onLicenseRefetch?.();
  };

  const [contactDataLocal, contactStatus, { setData: setContactDataLocal }] = useDataFetcher<ContactInformationData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'contactInformation'),
    EMPTY_CONTACT,
    defaultDataTransform,
    undefined,
    !!id && !contactDataProp,
    '3.0'
  );

  const contactData = contactDataProp ?? contactDataLocal;
  const isContactLoading = isContactLoadingProp ??
    (contactStatus === AsyncStatus.Idle || contactStatus === AsyncStatus.Pending);

  // Optimistic update: the save payload already reflects what the server just persisted,
  // so we apply it directly instead of round-tripping through a refetch.
  const handleContactSaveSuccess = (updated: ContactInformationData) => {
    setActiveSection(null);
    setContactDataLocal((prev) => ({ ...prev, ...updated }));
    onContactSaved?.(updated);
  };

  const [ratesDataLocal, ratesStatus, { getData: refetchRatesLocal }] = useDataFetcher<RatesData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'rates'),
    EMPTY_RATES,
    defaultDataTransform,
    undefined,
    !!id && !ratesDataProp,
    '3.0'
  );

  const ratesData = ratesDataProp ?? ratesDataLocal;
  const isRatesLoadingLocal = ratesDataProp
    ? false
    : (ratesStatus === AsyncStatus.Idle || ratesStatus === AsyncStatus.Pending);

  const refetchRates = () => {
    refetchRatesLocal();
    onRatesRefetch?.();
  };

  const [unavailabilityData, , { getData: refetchUnavailability }] = useDataFetcher<UnavailabilityEntry[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'unavailability'),
    EMPTY_UNAVAILABILITY,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  const [internalNoteData, , { getData: refetchInternalNote }] = useDataFetcher<InternalNoteData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'internalNote'),
    EMPTY_INTERNAL_NOTE,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  const [caseManagementData] = useDataFetcher<CaseManagementData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'caseManagement'),
    EMPTY_CASE_MANAGEMENT,
    defaultDataTransform,
    undefined,
    !!id && showCaseManagement,
    '3.0'
  );

  const [allDocumentsLocal, , { getData: refetchDocumentsLocal }] = useDataFetcher<ReviewerDocumentRow[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'documents'),
    [],
    defaultDataTransform,
    undefined,
    !!id && !documentsProp,
    '3.0'
  );

  const allDocuments = documentsProp ?? allDocumentsLocal;

  // Licenses/certifications saved from the drawers. The page owns the license data when it passes
  // it down, so the change is lifted there; otherwise it's patched into the local copy.
  const handleCredentialsChange = (stateLicenses: StateLicense[], boardCertifications: BoardCertification[]) => {
    if (onCredentialsSaved) {
      onCredentialsSaved(stateLicenses, boardCertifications);
      return;
    }
    setLicenseDataLocal((prev) => ({ ...prev, stateLicenses, boardCertifications }));
    refetchDocumentsLocal();
  };

  const recentFinancialDocuments = useMemo(
    () => ({
      feeSchedule: getLatestByType(allDocuments, REVIEWER_DOCUMENT_TYPE_FEE_SCHEDULE),
      contract: getLatestByType(allDocuments, REVIEWER_DOCUMENT_TYPE_CONTRACT),
      addendum: getLatestByType(allDocuments, REVIEWER_DOCUMENT_TYPE_ADDENDUM),
    }),
    [allDocuments]
  );

  const ratesDataWithDocs: RatesData = useMemo(
    () => ({ ...ratesData, recentFinancialDocuments }),
    [ratesData, recentFinancialDocuments]
  );

  // Reviewer type is edited on the Rates card, so prefer its data: it is refetched right after a
  // Rates save, letting the IME Locations card appear/disappear without a page reload.
  const showImeLocations = isImeType(
    ratesData?.reviewerTypeValue ?? licenseData?.reviewerTypeValue
  );

  const showExpiredCredentialsBanner = licenseData?.credentialsAutoHoldEnabled !== false && hasExpiredLicenseOrCertification(
    licenseData?.stateLicenses ?? [],
    licenseData?.boardCertifications ?? []
  );

  const [imeLocationsData, , { getData: refetchImeLocations }] = useDataFetcher<ImeLocation[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'imeLocations'),
    EMPTY_IME_LOCATIONS,
    defaultDataTransform,
    undefined,
    !!id && showImeLocations,
    '3.0'
  );

  return (
    <>
      {showExpiredCredentialsBanner && (
        <div className="reviewer-overview-banner">
          <Banner
            variant="error"
            title="License or board certification expired"
            description="Update your license or board certification to continue being assigned to cases."
          />
        </div>
      )}
      <div className="reviewer-overview">
      {/* Left column — cards stack independently of the right column's height */}
      <div className="reviewer-overview__left-col">
      <FormSection
        title={Sections.LicenseInformation}
        isActive={activeSection === Sections.LicenseInformation}
        onActive={() => setActiveSection(Sections.LicenseInformation)}
        initializedByDefault
        readOnlyContent={
          isLicenseLoadingLocal
            ? <SkeletonCard lines={[4, 3]} />
            : <LicenseInformationReadOnly
                data={licenseData}
                reviewerId={id}
                onCredentialsChange={handleCredentialsChange}
              />
        }
        activeContent={
          <LicenseInformationActive
            data={licenseData}
            reviewerId={id}
            onCancel={() => setActiveSection(null)}
            onSaveSuccess={() => { setActiveSection(null); refetchLicense(); }}
            onCredentialsChange={handleCredentialsChange}
          />
        }
      />

      <FormSection
        title={Sections.ContactInformation}
        isActive={activeSection === Sections.ContactInformation}
        onActive={() => canEditContact && setActiveSection(Sections.ContactInformation)}
        hideEditIcon={!canEditContact}
        initializedByDefault
        readOnlyContent={
          isContactLoading
            ? <SkeletonCard lines={[4, 3]} />
            : <ContactInformationReadOnly
                data={contactData}
                reviewerId={id}
                reviewerStatus={reviewerStatusValue}
              />
        }
        activeContent={
          <ContactInformationActive
            data={contactData}
            reviewerId={id}
            reviewer={reviewer}
            onCancel={() => setActiveSection(null)}
            onSaveSuccess={handleContactSaveSuccess}
          />
        }
      />
      </div>

      {/* Right column */}
      <div className="reviewer-overview__right-col">
        {showCaseManagement && (
          <FormSection
            title="Case Management"
            isActive={false}
            onActive={() => {}}
            initializedByDefault
            hideEditIcon
            readOnlyContent={
              <CaseManagementContent data={caseManagementData ?? EMPTY_CASE_MANAGEMENT} />
            }
          />
        )}
        <FormSection
          title={Sections.Unavailability}
          isActive={false}
          onActive={() => {}}
          initializedByDefault
          hideEditIcon
          readOnlyContent={
            <UnavailabilityContent
              entries={unavailabilityData ?? EMPTY_UNAVAILABILITY}
              reviewerId={id}
              onRefresh={refetchUnavailability}
            />
          }
        />
        <FormSection
          title={Sections.Rates}
          isActive={activeSection === Sections.Rates}
          onActive={() => canEditRates && setActiveSection(Sections.Rates)}
          hideEditIcon={!canEditRates}
          initializedByDefault
          readOnlyContent={
            isRatesLoadingLocal
              ? <SkeletonCard lines={[2, 2]} />
              : <RatesReadOnly data={ratesDataWithDocs} reviewerId={id} />
          }
          activeContent={
            <RatesActive
              data={ratesDataWithDocs}
              reviewerId={id}
              onCancel={() => setActiveSection(null)}
              onSaveSuccess={() => { setActiveSection(null); refetchRates(); }}
            />
          }
        />
        {showImeLocations && (
          <FormSection
            title="IME Locations"
            isActive={false}
            onActive={() => {}}
            initializedByDefault
            hideEditIcon
            readOnlyContent={
              <ImeLocationsContent
                entries={imeLocationsData ?? EMPTY_IME_LOCATIONS}
                reviewerId={id}
                onRefresh={refetchImeLocations}
              />
            }
          />
        )}
        <FormSection
          title={Sections.InternalNote}
          isActive={activeSection === Sections.InternalNote}
          onActive={() => canEditInternalNote && setActiveSection(Sections.InternalNote)}
          hideEditIcon={!canEditInternalNote}
          initializedByDefault
          readOnlyContent={<InternalNoteReadOnly data={internalNoteData} />}
          activeContent={
            <InternalNoteActive
              data={internalNoteData}
              reviewerId={id}
              onCancel={() => setActiveSection(null)}
              onSaveSuccess={() => { setActiveSection(null); refetchInternalNote(); }}
            />
          }
        />
      </div>
      </div>
    </>
  );
};

export default ReviewerProfileOverview;
